import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import * as Highcharts from 'highcharts'
import 'highcharts/modules/accessibility'
import { HighchartsChartModule } from 'highcharts-angular'

import { ProjectProgress } from '../../../../services/trygroup-dashboard/trygroup-personal-analysis.service'

const CHART_HEIGHT = 360
const DAY_MS = 24 * 60 * 60 * 1000
const JST_OFFSET_MS = 9 * 60 * 60 * 1000
const ACTUAL_SERIES_NAME = '15回移動平均スコア（実力線）'
const PLAN_SERIES_NAME = '進捗計画線'
const TARGET_SERIES_NAME = '目標スコア'

@Component({
  selector: 'app-trygroup-project-progress-chart',
  standalone: true,
  imports: [HighchartsChartModule],
  template: `
    <div class="project-progress-chart" #container>
      <highcharts-chart
        [Highcharts]="Highcharts"
        [options]="options"
        [callbackFunction]="chartCallback"
      />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .project-progress-chart {
        width: 100%;
        overflow-x: hidden;
      }

      highcharts-chart {
        display: block;
        width: 100%;
        height: ${CHART_HEIGHT}px;
      }
    `,
  ],
})
export class TrygroupProjectProgressChartComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input({ required: true }) projectProgress!: ProjectProgress
  @ViewChild('container') container!: ElementRef

  chart: Highcharts.Chart | undefined = undefined
  readonly Highcharts = Highcharts
  options!: Highcharts.Options

  private timerId: ReturnType<typeof setTimeout> | null = null
  private readonly observer: ResizeObserver = new ResizeObserver(() => {
    if (this.chart && this.container?.nativeElement?.offsetParent != null) {
      this.chart.reflow()
    }
  })

  ngAfterViewInit(): void {
    this.timerId = setTimeout(() => {
      this.observer.observe(this.container?.nativeElement)
      this.timerId = null
    }, 100)
  }

  ngOnDestroy(): void {
    if (this.timerId != null) {
      clearTimeout(this.timerId)
    }
    this.observer.disconnect()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectProgress']) {
      if (this.chart) {
        this.updateChart()
      } else {
        this.buildOptions()
      }
    }
  }

  chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chart = chart
    this.updateChart()
  }

  private buildOptions(): void {
    this.options = {
      chart: {
        type: 'line',
        height: CHART_HEIGHT,
        spacing: [16, 16, 16, 16],
      },
      credits: { enabled: false },
      title: { text: undefined },
      xAxis: {
        type: 'datetime',
        labels: {
          format: '{value:%m/%d}',
          style: { fontSize: '11px', color: '#666' },
        },
      },
      yAxis: {
        title: { text: undefined },
        min: 0,
        max: 100,
        tickPositions: [0, 25, 50, 75, 100],
        gridLineDashStyle: 'Dash',
        labels: {
          format: '{value}点',
          style: { fontSize: '11px', color: '#666' },
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        xDateFormat: '%Y-%m-%d',
        formatter: function () {
          const points = this.points
          if (!points || points.length === 0) {
            return ''
          }

          let html = `<div style="font-size:13px;font-weight:bold;margin-bottom:4px">${Highcharts.dateFormat(
            '%Y-%m-%d',
            Number(this.x),
          )}</div>`

          for (const point of points) {
            const value = point.y
            if (value == null) {
              continue
            }

            html += `<div style="color:${point.color}">${point.series.name}：${value.toFixed(
              1,
            )}点</div>`
          }

          return html
        },
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: { fontSize: '11px' },
      },
      plotOptions: {
        series: {
          connectNulls: true,
          marker: { symbol: 'circle' },
        },
      },
      series: [],
    }
  }

  private updateChart(): void {
    if (!this.chart) {
      return
    }

    const progress = this.projectProgress
    const min = addDaysToTimestamp(progress.kickoffDate, -10)
    const kickoff = dateOnlyToTimestamp(progress.kickoffDate)
    const end = dateOnlyToTimestamp(progress.endDate)
    const today = dateOnlyToTimestamp(toTodayDateOnly())
    const series = this.buildSeries(min, end)

    this.chart.update(
      {
        xAxis: {
          min,
          max: end,
          plotBands: [
            {
              from: min,
              to: kickoff,
              color: 'rgba(148, 163, 184, 0.12)',
            },
          ],
          plotLines: [
            {
              value: kickoff,
              width: 1,
              color: '#64748b',
              dashStyle: 'Dash',
              zIndex: 4,
            },
            {
              value: today,
              width: 2,
              color: '#ef4444',
              zIndex: 5,
            },
          ],
        },
        series,
      },
      true,
      true,
    )
  }

  private buildSeries(
    min: number,
    end: number,
  ): Highcharts.SeriesOptionsType[] {
    const progress = this.projectProgress
    const series: Highcharts.SeriesOptionsType[] = [
      {
        name: ACTUAL_SERIES_NAME,
        type: 'line',
        color: '#1e3a5f',
        lineWidth: 3,
        marker: { radius: 4, symbol: 'circle' },
        data: progress.dataPoints.map((point) => [
          dateOnlyToTimestamp(point.date),
          roundScore(point.overallScore),
        ]),
      },
      {
        name: TARGET_SERIES_NAME,
        type: 'line',
        color: '#16a34a',
        dashStyle: 'Dash',
        lineWidth: 2,
        marker: { enabled: false },
        enableMouseTracking: false,
        data: [
          [min, progress.targetScore],
          [end, progress.targetScore],
        ],
      },
    ]

    if (progress.kickoffScore != null) {
      series.splice(1, 0, {
        name: PLAN_SERIES_NAME,
        type: 'line',
        color: '#94a3b8',
        dashStyle: 'ShortDash',
        lineWidth: 2,
        marker: { enabled: false },
        data: [
          [dateOnlyToTimestamp(progress.kickoffDate), progress.kickoffScore],
          [end, progress.targetScore],
        ],
      })
    }

    return series
  }
}

function roundScore(value: number | null | undefined): number | null {
  return value != null ? Math.round(value * 10) / 10 : null
}

function addDaysToTimestamp(dateOnly: string, days: number): number {
  return dateOnlyToTimestamp(dateOnly) + days * DAY_MS
}

function dateOnlyToTimestamp(dateOnly: string): number {
  const [year, month, day] = dateOnly.split('-').map(Number)
  return Date.UTC(year, month - 1, day)
}

function toTodayDateOnly(): string {
  const jstToday = new Date(Date.now() + JST_OFFSET_MS)
  return `${jstToday.getUTCFullYear()}-${String(
    jstToday.getUTCMonth() + 1,
  ).padStart(2, '0')}-${String(jstToday.getUTCDate()).padStart(2, '0')}`
}
